/**
 * ドロップの解決（入れ替え・押しのけ）。
 *
 * 押しのけ規則は 1 つだけである（`04a` §3.2）:
 *
 * > 押しのけられたピースは、ドラッグされたグループの移動ベクトルの逆方向へ、
 * > ドラッグされたグループの新しい footprint の外に出るまで移動する。
 *
 * 連鎖の解決も衝突検査も要らない。盤内に収まる移動であれば必ず成功し（`04a` §3.4 の定理、
 * 不変条件 I-7）、失敗理由は `OUT_OF_BOARD` の 1 種類しかない。単一ピースのドラッグはこの
 * 規則の特殊ケースとして任意 2 セルの互換になり、互換が対称群を生成することから
 * **盤面の可解性が構造的に保証される**。
 *
 * 根拠: spec/04a-puzzle-core.md §3、02-requirements.md FR-008 / FR-010 / FR-011、ADR-010。
 */

import { positionsOf } from './board';
import { cellOf, col, inBoard, row } from './grid';
import { computeGroups } from './groups';
import { at } from './internal';
import type { CellIndex, PieceId, Placement, TargetLayout } from './types';

/** セル単位の移動ベクトル（列方向・行方向）。 */
export interface MoveVector {
  readonly dc: number;
  readonly dr: number;
}

/** ドロップの結果。失敗は盤外へのはみ出しのみ。 */
export type DropResult =
  | { readonly ok: true; readonly placement: Placement }
  | { readonly ok: false; readonly reason: 'OUT_OF_BOARD' };

/**
 * ドラッグされたピース群を `v` だけ動かし、押しのけを解決した新しい配置を返す。
 *
 * 入力の `placement` は変更しない。失敗時は新しい配列すら作らない。
 *
 * @param placement 現在の配置（セル -> ピース）
 * @param draggedPieces ドラッグされたグループの構成ピース
 * @param v 移動ベクトル
 * @param C 列数
 * @param R 行数
 */
export function resolveDrop(
  placement: Placement,
  draggedPieces: PieceId[],
  v: MoveVector,
  C: number,
  R: number,
): DropResult {
  const positionOf = positionsOf(placement);

  // S = ドラッグされたピースが今いるセル、S' = その移動先。
  const source = new Set<CellIndex>();
  for (const piece of draggedPieces) source.add(at(positionOf, piece));

  const destination = new Set<CellIndex>();
  for (const cell of source) {
    const toCol = col(cell, C) + v.dc;
    const toRow = row(cell, C) + v.dr;
    // 剰余ではなく列・行で判定する。剰余だと行をまたいだ折り返しを盤内と誤認する。
    if (!inBoard(toCol, toRow, C, R)) return { ok: false, reason: 'OUT_OF_BOARD' };
    destination.add(cellOf(toCol, toRow, C));
  }

  // 移動量 0 は盤面を変えない。ここで抜けないと、下の脱出ループが進まなくなる。
  if (v.dc === 0 && v.dr === 0) return { ok: true, placement: [...placement] };

  const next = [...placement];

  // 1. ドラッグされたピースを移す。
  for (const piece of draggedPieces) {
    const from = at(positionOf, piece);
    next[cellOf(col(from, C) + v.dc, row(from, C) + v.dr, C)] = piece;
  }

  // 2. 押しのけられたピースを −v 方向へ、S' の外に出るまで逃がす。
  //    §3.4 の証明により、終点は必ず S \ S'（ドラッグされたグループが空けたセル）になる。
  for (const cell of destination) {
    if (source.has(cell)) continue; // ドラッグ対象自身がいたセルは押しのけの対象ではない
    const displaced = at(placement, cell);
    next[escapeCell(cell, v, destination, C, placement.length)] = displaced;
  }

  return { ok: true, placement: next };
}

/**
 * `cell` から `−v` 方向へ進み、`destination`（= S'）の外に出た最初のセルを返す。
 *
 * 自己重なりのない移動（`S ∩ S' = ∅`）では必ず 1 歩で出る。自己重なりがある場合の歩数は
 * `v` に平行な直線ごとに異なり、その直線上で `S` が作る極大連続区間の長さに等しい（`04a` §3.5）。
 * 「端のピースが端の空欄へ回り込む」挙動はここから導かれる。
 */
function escapeCell(
  cell: CellIndex,
  v: MoveVector,
  destination: ReadonlySet<CellIndex>,
  C: number,
  N: number,
): CellIndex {
  let current = cell;
  // 上限は盤面のセル数。定理により実際にはこれより手前で必ず抜ける。
  for (let step = 0; step < N; step++) {
    current = cellOf(col(current, C) - v.dc, row(current, C) - v.dr, C);
    if (!destination.has(current)) return current;
  }
  // 到達しない。到達したら S' の構成か移動ベクトルが壊れている。
  /* v8 ignore next */
  throw new Error(`不変条件 I-7 違反: セル ${cell} から S' の外へ脱出できませんでした。`);
}

/**
 * ドロップの予測（`04a` §3.5「ドロップの予測」）。
 *
 * 押しのけの影響を受けるグループは 2 通りにしか分かれない。動くグループは、全員が同じ
 * ベクトルで動いて維持されるか、移動量が食い違って分解されるかのどちらかであり、
 * 「押しのけられるのに何も起きない」という第 3 の状態は存在しない。
 */
export interface DropPreview {
  /** このドロップで分解されるグループ（構成ピースの配列）。 */
  readonly decomposed: readonly (readonly PieceId[])[];
  /** 押しのけられるが維持されるグループ。 */
  readonly displaced: readonly (readonly PieceId[])[];
}

/**
 * 「いま指を離すと何が起きるか」を、盤面を変更せずに求める（FR-011）。
 *
 * 新しい規則ではなく既存の規則の帰結である。`resolveDrop` で得た次の配置を
 * `computeGroups` で数え直すだけで、分解の判断を自前に持たない。盤外へのドロップは
 * 成立しないため、両方とも空を返す（枠は自動的に消える）。
 *
 * 対象から除くもの（`04a` §3.5）:
 * - ドラッグされたグループ自身（剛体として動き、分解されない）
 * - 単独ピース（維持も分解も意味を持たない）
 * - 位置の変わらないグループ（相対位置が変わらず、結合が保たれる）
 */
export function previewDrop(
  placement: Placement,
  target: TargetLayout,
  dragged: PieceId[],
  v: MoveVector,
  C: number,
  R: number,
): DropPreview {
  const next = resolveDrop(placement, dragged, v, C, R);
  if (!next.ok) return { decomposed: [], displaced: [] };

  const before = computeGroups(placement, target, C);
  const after = computeGroups(next.placement, target, C);
  const positionBefore = positionsOf(placement);
  const positionAfter = positionsOf(next.placement);
  const draggedSet = new Set(dragged);

  const decomposed: (readonly PieceId[])[] = [];
  const displaced: (readonly PieceId[])[] = [];
  for (const members of before.members) {
    if (members.length < 2) continue;
    if (members.some((piece) => draggedSet.has(piece))) continue;
    if (members.every((piece) => at(positionBefore, piece) === at(positionAfter, piece))) continue;

    const label = at(after.labelOf, at(members, 0));
    const stays = members.every((piece) => at(after.labelOf, piece) === label);
    (stays ? displaced : decomposed).push(members);
  }
  return { decomposed, displaced };
}
